import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete,Button,Dialog,DialogActions,DialogContent,DialogTitle,Skeleton,Stack,TextField } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useMutation,useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AttToken from '../../../helpers/attToken';
import { trackEventMatomo } from '../../../helpers/matomo';
import NovaNotificacao,{ newNotificationType } from '../../../service/NovaNotificacao';
import queryClient from '../../../service/query';
import ServiceTypes,{ types } from '../../../service/ServiceTypes';

type props = {
  isEdit: boolean,
  notificationEdit: newNotificationType | undefined,
  setNotificationEdit: Function,
  openModal: boolean,
  setOpenModal: Function,
};

const serviceType = new ServiceTypes();
const notifService = new NovaNotificacao();

export default function NewNotificationsModal({
  isEdit,
  notificationEdit,
  setNotificationEdit,
  openModal,
  setOpenModal }: props) {
  const { t } = useTranslation('translation');

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['SERVICES_LIST'],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token não encontrado');
        if (token) {
          const body = { ...token, empresa: token.userEmpresa };
          const result = await serviceType.getAllByCompany(body);
          if (!result) throw new Error('Erro ao buscar tipos de serviço');
          return result;
        }
      } catch (err) {
        throw new Error('Erro ao buscar tipos de serviço');
      }
    },
    retry: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  }
  );

  const { mutate, isPending } = useMutation({
    mutationKey: [isEdit ? 'EDIT_NOTIFICATION_MODEL' : 'NEW_NOTIFICATION_MODEL'],
    mutationFn: async () => {
      try {
        const promise = toast.promise(async () => {
          const token = await AttToken();
          if (!token) throw new Error('Token não encontrado');
          if (isEdit) {
            const response = await notifService.edit(token, notificationEdit as newNotificationType);
            if (!response) throw new Error('Erro ao editar modelo de notificação');
            return response;
          } else {
            const response = await notifService.create(token, notificationEdit as newNotificationType);
            if (!response) throw new Error('Erro ao criar modelo de notificação');
            return response;
          }
        }, {
          pending: isEdit ? t('notifications.newModificationModal.editingNotification') : t('notifications.newModificationModal.creatingNewNotificationModel') as string,
          error: isEdit ? t('notifications.newModificationModal.errorChangingNotification') : t('notifications.newModificationModal.errorCreatingNotificationModel') as string,
          success: isEdit ? t('notifications.newModificationModal.notificationChanged') : t('notifications.ticket.ModeloCriado') as string,
        }, { toastId: isEdit ? 'EDIT_NOTIFICATION_MODEL' : 'NEW_NOTIFICATION_MODEL' });
        return promise;
      } catch (err) {
        throw new Error('Erro ao criar/editar modelo de notificação');
      }
    },
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ['GET_NOTIFICATION_LIST'] });
      setOpenModal(false);
    }
  });

  const onLoading = isLoading || isFetching || isPending;

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      open={openModal}
      onClose={() => {
        trackEventMatomo('modal criação de notificação', 'click', 'modal', 'fecha modal');
        setOpenModal(false);
      }}
      scroll={'paper'}
    >
      <DialogTitle>{!isEdit
        ? t('notifications.ticket.criarModeloNot')
        : t('notifications.ticket.editarModeloNoti')}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => {
          trackEventMatomo('modal criação de notificação', 'click', 'botão', 'fecha modal');
          setOpenModal(false);
        }}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        <Stack direction='column' spacing={2} width='100%' alignItems='flex-start' justifyContent='flex-start'>
          <TextField
            onClick={() => trackEventMatomo('modal criação de notificação', 'click', 'input', 'edita valor')}
            fullWidth
            label={t('name')}
            value={notificationEdit ? notificationEdit?.name : undefined}
            onChange={(event) => setNotificationEdit({ ...notificationEdit, name: event.target.value })}
          />
          <TextField
            onClick={() => trackEventMatomo('modal criação de notificação', 'click', 'input', 'edita valor')}
            multiline
            fullWidth
            rows={2}
            maxRows={4}
            value={notificationEdit?.message}
            label={t('message')}
            onChange={(event) => setNotificationEdit({ ...notificationEdit, message: event.target.value })}
          />
          {onLoading ? <Skeleton variant='rounded' width='100%' height={56} /> :
            <Autocomplete
              fullWidth
              onClick={() => trackEventMatomo('modal criação de notificação', 'click', 'select', 'seleciona valor')}
              defaultValue={notificationEdit?.serviceType ? {
                value: notificationEdit?.serviceType as string,
                label: data?.find((ele: types) => ele.tipoId === notificationEdit?.serviceType)?.nome
              } : null}
              filterSelectedOptions
              options={
                data
                  ?.sort((a: types, b: types) => { return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : -1; })
                  ?.map((ele: types) => ({
                    label: ele.nome,
                    value: ele.tipoId,
                  }))}
              onChange={(_event, newValue: { label: string, value: string } | null) => {
                setNotificationEdit({ ...notificationEdit, serviceType: newValue?.value });
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.value}>
                  <span>{option.label}</span>
                </li>)}
              renderInput={(params) => <TextField {...params} label={t('notifications.ticket.tipoServico')} />}
            />
          }
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            mutate();
            trackEventMatomo('modal criação de notificação', 'click', 'button', 'salva');
          }}
          disabled={
            onLoading ||
            (!notificationEdit?.name?.length) ||
            (!notificationEdit?.message?.length) ||
            (!notificationEdit?.serviceType?.length)
          }>

          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
